import styled from "@emotion/styled";
import React, { memo } from "react";
import { components, OptionProps } from "react-select";
import { areEqual } from "react-window";
import * as colors from "../../../constants/colors";
import { sizes } from "../../../constants/spacing";
import { OptionType } from "../types";
import { CheckboxOption } from "./CheckboxOption";
import { SelectAllContext } from "./SelectAllContext";

export const DEFAULT_ALL_OPTION_VALUE = "LODESTAR_CORE_SELECT_ALL_VALUE_DO_NOT_USE_OR_YOU_WILL_BE_FIRED";

const Separator = styled("div")`
    height: 2px;
    background-color: ${colors.neutralLight20};
    margin: 11px ${sizes.medium}px;
`;

const getLengthIfArray = (value: unknown) => (Array.isArray(value) && value.length) || 0;

/**
 * Custom comparison function that ignores cx and getValue, 2 props that we don't use!
 *  - The cx prop is updated EVERY render in the Select.js component of react-select in `getCommonProps`.
 *      It should only update really when the `classNamePrefix` prop changes, but we don't allow that prop
 *      so we should be fine there.
 *  - The `getValue` prop is an imperative reference to the currently selected value of the Select.
 *      It unecessarily re-creates itself as an anon fn in `getCommonProps` as well (ugh).
 * Once this is in, we should be able to remove this optimization:
 * https://github.com/JedWatson/react-select/pull/4084
 */
function propsAreEqualWithoutProblemProps(
    p1: Readonly<OptionProps<OptionType>>,
    p2: Readonly<OptionProps<OptionType>>
) {
    const { cx: _cx1, getValue: _getValue1, ...restP1 } = p1;
    const { cx: _cx2, getValue: _getValue2, ...restP2 } = p2;
    return areEqual(restP1, restP2);
}

export const MultiSelectOption: typeof components.Option = memo((props: OptionProps<OptionType>) => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { selectProps, data, isSelected } = props;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { value, options } = selectProps;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (data.value === DEFAULT_ALL_OPTION_VALUE) {
        // We are rendering the "Select All" option
        // Determine if it should be checked
        const totalCount = (options && getLengthIfArray(options) - 1) || 0;
        const allSelected = options != null && getLengthIfArray(value) === totalCount; // Account for the select all option.

        const noneSelected = value === null || (Array.isArray(value) && value.length === 0);

        const selectedCount = getLengthIfArray(value);

        const checkedState = allSelected ? "checked" : noneSelected ? "unchecked" : "indeterminate";

        return (
            <>
                <SelectAllContext.Consumer>
                    {({ getText: getSelectAllText }) => (
                        <CheckboxOption {...props} isSelected={allSelected} checkedState={checkedState}>
                            {getSelectAllText(selectedCount, totalCount)}
                        </CheckboxOption>
                    )}
                </SelectAllContext.Consumer>
                <Separator />
            </>
        );
    } else {
        return <CheckboxOption {...props} checkedState={isSelected ? "checked" : "unchecked"} />;
    }
}, propsAreEqualWithoutProblemProps);

MultiSelectOption.displayName = "MultiSelectOption";
